import React, { useState, useCallback } from 'react';

const FormValidator = ({ 
  children, 
  validationRules = {}, 
  onValidationChange,
  showErrorsOnSubmit = true 
}) => {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateField = useCallback((name, value, rules) => {
    const fieldRules = rules[name];
    if (!fieldRules) return null;

    // Required validation
    if (fieldRules.required && (!value || value.toString().trim() === '')) {
      return fieldRules.messages?.required || `${name} is required`;
    }

    // Email validation
    if (fieldRules.email && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return fieldRules.messages?.email || 'Please enter a valid email address';
      }
    }

    // Password validation
    if (fieldRules.password && value) {
      if (fieldRules.minLength && value.length < fieldRules.minLength) {
        return fieldRules.messages?.minLength || `Password must be at least ${fieldRules.minLength} characters`;
      }
      if (fieldRules.pattern && !fieldRules.pattern.test(value)) {
        return fieldRules.messages?.pattern || 'Password does not meet requirements';
      }
    }

    // Custom validation function
    if (fieldRules.custom && typeof fieldRules.custom === 'function') {
      const customError = fieldRules.custom(value);
      if (customError) return customError;
    }

    return null;
  }, []);

  const validateForm = useCallback((formData) => {
    const newErrors = {};
    let isValid = true;

    Object.keys(validationRules).forEach(fieldName => {
      const error = validateField(fieldName, formData[fieldName], validationRules);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    if (onValidationChange) {
      onValidationChange(isValid, newErrors);
    }

    return isValid;
  }, [validationRules, validateField, onValidationChange]);

  const handleFieldChange = useCallback((name, value) => {
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Validate field in real-time if it was previously touched or submitted
    if (touched[name] || isSubmitted) {
      const error = validateField(name, value, validationRules);
      if (error) {
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  }, [errors, touched, isSubmitted, validateField, validationRules]);

  const handleFieldBlur = useCallback((name, value) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name, value, validationRules);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  }, [validateField, validationRules]);

  const handleSubmit = useCallback((formData) => {
    setIsSubmitted(true);
    return validateForm(formData);
  }, [validateForm]);

  const clearErrors = useCallback(() => {
    setErrors({});
    setTouched({});
    setIsSubmitted(false);
  }, []);

  const getFieldError = useCallback((name) => {
    return errors[name] || null;
  }, [errors]);

  const hasFieldError = useCallback((name) => {
    return Boolean(errors[name]);
  }, [errors]);

  const shouldShowError = useCallback((name) => {
    return (touched[name] || isSubmitted) && Boolean(errors[name]);
  }, [touched, isSubmitted, errors]);

  return (
    <>
      {children({
        errors,
        touched,
        isSubmitted,
        validateForm,
        handleFieldChange,
        handleFieldBlur,
        handleSubmit,
        clearErrors,
        getFieldError,
        hasFieldError,
        shouldShowError
      })}
    </>
  );
};

// Error message component
export const ErrorMessage = ({ 
  error, 
  show = true, 
  className = '',
  animate = true 
}) => {
  if (!show || !error) return null;

  return (
    <div 
      className={`
        text-sm text-error mt-1 flex items-center gap-1
        ${animate ? 'animate-shake' : ''}
        ${className}
      `}
      role="alert"
      aria-live="polite"
    >
      <svg 
        className="w-4 h-4 flex-shrink-0" 
        fill="currentColor" 
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path 
          fillRule="evenodd" 
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
          clipRule="evenodd" 
        />
      </svg>
      <span>{error}</span>
    </div>
  );
};

// Success message component
export const SuccessMessage = ({ 
  message, 
  show = true, 
  className = '' 
}) => {
  if (!show || !message) return null;

  return (
    <div 
      className={`
        text-sm text-success mt-1 flex items-center gap-1
        ${className}
      `}
      role="status"
      aria-live="polite"
    >
      <svg 
        className="w-4 h-4 flex-shrink-0" 
        fill="currentColor" 
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path 
          fillRule="evenodd" 
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
          clipRule="evenodd" 
        />
      </svg>
      <span>{message}</span>
    </div>
  );
};

export default FormValidator;

